/**
 * InfraSys Storage Inventory - Backend / Architecture Info
 *
 * A read-only reference page describing how Storage Inventory works in
 * production: a React SPA that calls API Gateway -> Lambda over a stable
 * /inventory/* HTTP contract, with asset records + site settings in DynamoDB
 * and photo blobs in S3 (served via short-lived presigned URLs). Requests are
 * authenticated with SigV4 credentials obtained from a Midway OIDC token via
 * STS AssumeRoleWithWebIdentity.
 *
 * The values here mirror the source of truth in:
 *   - the CDK infrastructure package (DynamoDB table, S3 bucket, IAM, API Gateway)
 *   - the Lambda handler (the /inventory/* routes)
 *   - src/lib/apiClient.ts (Midway -> STS -> SigV4 signing)
 * If those change, update this page to match.
 */
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Box from '@cloudscape-design/components/box';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import ExpandableSection from '@cloudscape-design/components/expandable-section';

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

const API_ROUTES = [
  { method: 'GET', path: '/inventory/scan?category=...', desc: 'List a category (returns an object for settings). Backed by DynamoDB Query on PK=category' },
  { method: 'POST', path: '/inventory/create', desc: 'Create one asset - body: { category, item }. Backend generates the UUID (SK)' },
  { method: 'POST', path: '/inventory/update', desc: 'Full replace by id - body: { category, item (must include id) }' },
  { method: 'POST', path: '/inventory/delete', desc: 'Delete by id - body: { category, id }. (S3 photo cleanup is a planned follow-up, not yet implemented)' },
  { method: 'POST', path: '/inventory/bulk', desc: 'Bulk create - body: { category, items[] } (DynamoDB BatchWrite)' },
  { method: 'POST', path: '/inventory/photo', desc: 'Get a presigned S3 PUT URL (or accept a blob) for a new photo. Returns its key' },
  { method: 'GET', path: '/inventory/photo-url?key=...', desc: 'Resolve a stored key to a short-lived presigned S3 GET URL for display' },
];

const CONFIG_VALUES = [
  { name: 'settings.json → apiBaseUrl', desc: 'API Gateway base URL, fetched at runtime by apiClient.ts in production' },
  { name: 'App access role (IAM)', desc: 'Role assumed via STS AssumeRoleWithWebIdentity using the Midway OIDC token; grants execute-api access. Created in the CDK infrastructure.' },
  { name: 'VITE_STORAGE_INVENTORY_LOCAL', desc: 'Dev-only flag: routes reads/writes through the local dev proxy instead of the deployed API' },
];

export function StorageInventoryDev() {
  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="How InfraSys Storage Inventory works behind the scenes - API Gateway + Lambda, DynamoDB for asset records and settings, and S3 for photos via presigned URLs, authenticated with Midway-derived SigV4 credentials."
        >
          Storage Inventory - Backend / Architecture Info
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Architecture overview */}
        <Container header={<Header variant="h2">Architecture</Header>}>
          <SpaceBetween size="s">
            <Box>
              The browser (a Vite/React Cloudscape SPA) calls <strong>API Gateway</strong> over a
              stable <code>/inventory/*</code> contract. A <strong>Lambda</strong> handler stores
              asset records and site settings in <strong>DynamoDB</strong> and photo blobs in{' '}
              <strong>S3</strong>, referenced from each asset's <code>images</code> array by their
              key (never the bytes). Photos are uploaded and displayed via short-lived{' '}
              <strong>presigned URLs</strong>, so the app never proxies image bytes and the bucket
              stays private.
            </Box>
            <Box variant="code">
              Browser → API Gateway → Lambda ┬→ DynamoDB (assets + settings){'\n'}
              {'                               '}└→ S3 (photos, via presigned GET/PUT URLs)
            </Box>
          </SpaceBetween>
        </Container>

        {/* Auth */}
        <Container
          header={
            <Header variant="h2" actions={<Badge color="red">Auth</Badge>}>
              Authentication (Midway → STS → SigV4)
            </Header>
          }
        >
          <SpaceBetween size="m">
            <ColumnLayout columns={2} variant="text-grid">
              <KV label="Identity">
                Midway OIDC id_token fetched from <code>midway-auth.amazon.com</code>
              </KV>
              <KV label="Credentials">
                STS <code>AssumeRoleWithWebIdentity</code> exchanges the token for temporary AWS
                credentials
              </KV>
              <KV label="Role">
                The application's least-privilege access role (created in CDK), grants{' '}
                <code>execute-api</code> invoke
              </KV>
              <KV label="Request signing">
                Requests to API Gateway are <strong>SigV4</strong>-signed (see{' '}
                <code>src/lib/apiClient.ts</code>)
              </KV>
            </ColumnLayout>
            <Box variant="small">
              Credentials are cached and refreshed before expiry. In local dev the app talks to the
              dev proxy instead and no SigV4 signing occurs.
            </Box>
          </SpaceBetween>
        </Container>

        {/* DynamoDB */}
        <Container
          header={
            <Header variant="h2" actions={<Badge color="blue">DynamoDB</Badge>}>
              DynamoDB (asset records + settings)
            </Header>
          }
        >
          <SpaceBetween size="m">
            <ColumnLayout columns={2} variant="text-grid">
              <KV label="Design">Single-table design</KV>
              <KV label="Partition key (PK)">
                <code>category</code> - servers | elementals | network_devices | settings
              </KV>
              <KV label="Sort key (SK)">
                <code>id</code> - UUID per asset (<code>singleton</code> for the settings record)
              </KV>
              <KV label="Attributes">
                Asset fields stored as real attributes (not an opaque JSON blob) so they can be
                filtered/projected
              </KV>
              <KV label="Main access pattern">
                "List all servers / elementals / network devices" → <code>Query</code> on{' '}
                <code>PK = category</code>
              </KV>
              <KV label="Secondary indexes">Add a GSI only when a new access pattern needs one</KV>
              <KV label="Capacity">On-demand to start</KV>
              <KV label="Protections">Encryption at rest (SSE) + point-in-time recovery (PITR) on</KV>
            </ColumnLayout>
            <Box variant="small">
              Item-level writes (per <code>id</code>) avoid whole-file read-modify-write races.
              Categories servers / elementals / network_devices hold lists of assets; settings is a
              single item at SK <code>singleton</code>.
            </Box>
          </SpaceBetween>
        </Container>

        {/* Photos / S3 */}
        <Container
          header={
            <Header variant="h2" actions={<Badge color="green">S3</Badge>}>
              Photo blobs (S3, presigned URLs)
            </Header>
          }
        >
          <SpaceBetween size="m">
            <ColumnLayout columns={2} variant="text-grid">
              <KV label="Bucket">Private (block public access), SSE enabled</KV>
              <KV label="Key layout">
                <code>photos/{'{category}'}/{'{uuid}'}.{'{ext}'}</code>
              </KV>
              <KV label="Stored on asset">
                Only the key string in <code>images</code> - never the bytes
              </KV>
              <KV label="Upload">
                <code>photo</code> returns a presigned <strong>PUT</strong> URL; the browser uploads
                directly to S3
              </KV>
              <KV label="Display">
                <code>photo-url</code> returns a short-lived presigned <strong>GET</strong> URL
              </KV>
              <KV label="Allowed types">PNG, JPEG (also webp/gif if uploaded)</KV>
            </ColumnLayout>
            <Box variant="small">
              Keys are validated to stay under the <code>photos/</code> prefix (no traversal).
              Cleaning up orphaned photos when an asset is updated or deleted is a planned
              follow-up and is not yet implemented in the Lambda backend.
            </Box>
          </SpaceBetween>
        </Container>

        {/* API routes */}
        <Container header={<Header variant="h2">API routes</Header>}>
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'method',
                header: 'Method',
                cell: (e) => (
                  <Badge color={e.method === 'GET' ? 'blue' : 'grey'}>{e.method}</Badge>
                ),
                width: 100,
              },
              { id: 'path', header: 'Route', cell: (e) => <code>{e.path}</code> },
              { id: 'desc', header: 'Description', cell: (e) => e.desc },
            ]}
            items={API_ROUTES}
            sortingDisabled
          />
          <Box variant="small" padding={{ top: 's' }}>
            The frontend speaks the same <code>/inventory/*</code> contract whether the backend is
            the deployed API (prod) or the dev proxy (local), so no UI code cares which is behind it.
          </Box>
        </Container>

        {/* Config */}
        <Container header={<Header variant="h2">Runtime configuration</Header>}>
          <SpaceBetween size="m">
            <Table
              variant="embedded"
              columnDefinitions={[
                { id: 'name', header: 'Setting', cell: (e) => <code>{e.name}</code>, width: 340 },
                { id: 'desc', header: 'Purpose', cell: (e) => e.desc },
              ]}
              items={CONFIG_VALUES}
              sortingDisabled
            />
            <ExpandableSection headerText="Local development">
              <SpaceBetween size="s">
                <Box>
                  Set <code>VITE_STORAGE_INVENTORY_LOCAL=true</code> in <code>.env.development</code>{' '}
                  and run the dev proxy (<code>npm run proxy</code>) to exercise the{' '}
                  <code>/inventory/*</code> contract without deploying.
                </Box>
                <Box variant="small" color="text-status-inactive">
                  Leaving the flag unset makes the app read static JSON with in-memory-only writes.
                </Box>
              </SpaceBetween>
            </ExpandableSection>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
}
